import { useCallback, useMemo, Context } from "react"
import type React from "react"
import ListItemDropDown from "components/list-item-dropdown"
import type { ListItemDropDownOption } from "components/list-item-dropdown/types"
import GraphTemplateSelector from "connected-components/graph/components/template-selector"
import PeriodDisplay from "connected-components/dashboard/components/period-display"
import { OK } from "helpers/data-statuses"
import { IDashboardContext } from "connected-components/dashboard/context/types"
import { useCategorySync } from "connected-components/graph/hooks/use-category-sync"
import { GraphContextValue } from "connected-components/graph/types"
import { useContentSetContext } from "./context"
import { useActiveContentSet } from "./use-active-content-set"

interface ListItemDropDownProps {
  label: string
  value: string | null
  setValue: (id: string) => void
  options: ListItemDropDownOption[]
  alignMenu?: "left" | "right"
  disablePortal?: boolean
  listSubheader?: string
  classes?: object
  disabled?: boolean
  disableSubHeader?: boolean
  disabledTooltip?: string
  listAction?: unknown
}

const TypedListItemDropDown = ListItemDropDown as React.ComponentType<ListItemDropDownProps>

interface ContentSetHeaderProps {
  context: IDashboardContext
  modelMetadata: Record<string, unknown>
}

export default function ContentSetHeader({ context }: ContentSetHeaderProps) {
  const {
    selectedCategory,
    setSelectedCategory,
    contentSetsStatus,
  } = useContentSetContext()

  const { activeContentSet } = useActiveContentSet(context as unknown as Context<GraphContextValue>)

  const categoryOptions = useMemo((): ListItemDropDownOption[] => {
    const basicOptions: ListItemDropDownOption[] = [{ id: "total", primaryText: "All Factors" }]
    if (!activeContentSet?.categories?.length) return basicOptions

    const modelCategories = activeContentSet.categories.map(category => ({
      id: category.id,
      primaryText: category.name,
    }))
    return [...basicOptions, ...modelCategories]
  }, [activeContentSet])

  const isCategoryOptionsReady = contentSetsStatus === OK
  useCategorySync(categoryOptions, selectedCategory, setSelectedCategory, isCategoryOptionsReady)

  const handleCategoryChange = useCallback((category: string) => {
    setSelectedCategory(category)
  }, [setSelectedCategory])

  return (
    <>
      <GraphTemplateSelector context={context} />
      <PeriodDisplay context={context} isPerformance={false} />
      <TypedListItemDropDown
        label="Category"
        value={selectedCategory}
        setValue={handleCategoryChange}
        options={categoryOptions}
        alignMenu="left"
        disablePortal
      />
    </>
  )
}
